import { DocumentNode, gql } from '@apollo/client';
import { Divider, HStack, Link, Stack, Text, VStack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Dinero, dinero, minimum, subtract } from 'dinero.js';
import * as React from 'react';

import { CurrencyDisplay } from '@/components/Currency';
import {
  AutoPayImportantDateType,
  PaymentImportantDateType,
  SimpleImportantDateType,
} from '@/graphql-types/globalTypes';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { InfoIcon } from '@/imports/ui/chakra/feather';
import { isGreaterThanZero } from '@/utilities';

import {
  ImportantDateItem_MembershipTermImportantDateFragment,
  ImportantDateItem_MembershipTermImportantDateFragment_AutoPayMembershipTermImportantDate,
  ImportantDateItem_MembershipTermImportantDateFragment_PaymentMembershipTermImportantDate,
  ImportantDateItem_MembershipTermImportantDateFragment_SimpleMembershipTermImportantDate,
} from './graphql-types/ImportantDateItem_MembershipTermImportantDateFragment';

dayjs.extend(isSameOrBefore);

export const ImportantDateItem: React.FC<{
  importantDate: ImportantDateItem_MembershipTermImportantDateFragment;
  creditBalance: Dinero<number>;
  isMembershipEndless: boolean;
  hasRelevantFailedPayment: boolean;
}> & { importantDateFragment: DocumentNode } = ({
  importantDate,
  creditBalance,
  isMembershipEndless,
  hasRelevantFailedPayment,
}) => {
  const { currency } = useDefaultCurrency();
  const totalPrice =
    importantDate.__typename === 'AutoPayMembershipTermImportantDate'
      ? dinero(importantDate.price)
      : importantDate.__typename === 'PaymentMembershipTermImportantDate'
      ? dinero(importantDate.annualPrice)
      : null;

  const monthlyPrice =
    importantDate.__typename === 'PaymentMembershipTermImportantDate' &&
    importantDate.monthlyPrice
      ? dinero(importantDate.monthlyPrice)
      : null;

  const dineroCreditToUse =
    creditBalance && totalPrice
      ? minimum([totalPrice, creditBalance])
      : dinero({ amount: 0, currency });

  const paymentReceived =
    (importantDate.__typename === 'PaymentMembershipTermImportantDate' &&
      importantDate.order?.paymentReceived) ||
    (importantDate.__typename === 'AutoPayMembershipTermImportantDate' &&
      importantDate.order?.paymentReceived);

  const dineroPaymentReceived = paymentReceived
    ? dinero(paymentReceived)
    : dinero({ amount: 0, currency });

  const dineroRemainingDue =
    totalPrice &&
    (isGreaterThanZero(dineroCreditToUse) ||
      isGreaterThanZero(dineroPaymentReceived))
      ? [totalPrice, dineroCreditToUse, dineroPaymentReceived].reduce(subtract)
      : dinero({ amount: 0, currency });

  return (
    <VStack w="full">
      <HStack w="full" justify="space-between" align="flex-end">
        <VStack align="start" spacing="0">
          <Text textStyle="compactStrong" textAlign="start">
            {dayjs(importantDate.date).format('MMM D, YYYY')}
          </Text>
          <ImportantDateType
            importantDate={importantDate}
            isMembershipEndless={isMembershipEndless}
            hasRelevantFailedPayment={hasRelevantFailedPayment}
          />
          {importantDate.__typename === 'PaymentMembershipTermImportantDate' &&
            importantDate.order &&
            importantDate.order.invoice &&
            importantDate.order.invoice.pdfUrl && (
              <Link
                href={importantDate.order.invoice.pdfUrl}
                fontSize="sm"
                fontWeight="normal"
                textDecoration="underline"
                color="gray.600"
              >
                Invoice {importantDate.order.invoice.number}
              </Link>
            )}
        </VStack>

        <Stack spacing="0" align="flex-end">
          {monthlyPrice && (
            <Text textStyle="detailStrong" color="gray.500">
              12 PAYMENTS OF{' '}
              <CurrencyDisplay
                color="gray.500"
                value={monthlyPrice}
                textStyle="detailStrong"
              />{' '}
              OR
            </Text>
          )}

          {!!totalPrice && (
            <CurrencyDisplay value={totalPrice} textStyle="bodyStrong" />
          )}
        </Stack>
      </HStack>

      {!!totalPrice &&
        (isGreaterThanZero(dineroCreditToUse) ||
          isGreaterThanZero(dineroPaymentReceived)) && (
          <VStack w="full" spacing="1">
            <Divider />

            {isGreaterThanZero(dineroCreditToUse) && (
              <HStack w="full" justify="space-between">
                <Text textStyle="detailStrong" color="gray.500">
                  CREDIT
                </Text>

                <HStack textStyle="bodyStrong" color="green.400" spacing="0">
                  <Text>-</Text>
                  <CurrencyDisplay value={dineroCreditToUse} />
                </HStack>
              </HStack>
            )}

            {isGreaterThanZero(dineroPaymentReceived) && (
              <HStack w="full" justify="space-between">
                <Text textStyle="detailStrong" color="gray.500">
                  TOTAL AMOUNT PAID
                </Text>

                <HStack textStyle="bodyStrong" color="green.400" spacing="0">
                  <Text>-</Text>
                  <CurrencyDisplay value={dineroPaymentReceived} />
                </HStack>
              </HStack>
            )}

            <HStack w="full" justify="space-between">
              <Text textStyle="detailStrong" color="gray.500">
                REMAINING AMOUNT DUE
              </Text>

              <CurrencyDisplay
                value={dineroRemainingDue}
                textStyle="bodyStrong"
              />
            </HStack>
          </VStack>
        )}
    </VStack>
  );
};

const ImportantDateType: React.FC<{
  importantDate:
    | ImportantDateItem_MembershipTermImportantDateFragment_SimpleMembershipTermImportantDate
    | ImportantDateItem_MembershipTermImportantDateFragment_PaymentMembershipTermImportantDate
    | ImportantDateItem_MembershipTermImportantDateFragment_AutoPayMembershipTermImportantDate;
  isMembershipEndless: boolean;
  hasRelevantFailedPayment: boolean;
}> = ({ importantDate, isMembershipEndless, hasRelevantFailedPayment }) => {
  const isPast = dayjs(importantDate.date).isSameOrBefore(dayjs());

  switch (importantDate.__typename) {
    case 'SimpleMembershipTermImportantDate':
      return (
        <SimpleMembershipTermImportantDateType
          importantDateType={importantDate.type}
          isPast={isPast}
          isMembershipEndless={isMembershipEndless}
        />
      );
    case 'AutoPayMembershipTermImportantDate':
      return (
        <AutoPayMembershipTermImportantDateType
          importantDateType={importantDate.autoPayType}
          numPaymentsRepresented={importantDate.numPaymentsRepresented}
          hasRelevantFailedPayment={hasRelevantFailedPayment}
        />
      );
    case 'PaymentMembershipTermImportantDate':
      return (
        <PaymentMembershipTermImportantDateType
          importantDateType={importantDate.paymentType}
          isPast={isPast}
        />
      );
    default:
      return <Text />;
  }
};

const SimpleMembershipTermImportantDateType: React.FC<{
  importantDateType: SimpleImportantDateType;
  isPast: boolean;
  isMembershipEndless: boolean;
}> = ({ importantDateType, isPast, isMembershipEndless }) => {
  switch (importantDateType) {
    case SimpleImportantDateType.GRACE_PERIOD_END:
      return <>{`${isPast ? 'Membership lapsed' : 'Grace period ends'}`}</>;
    case SimpleImportantDateType.TERM_END:
      return <>{`${isPast ? 'Membership lapsed' : 'Membership term ends'}`}</>;
    case SimpleImportantDateType.TERM_START:
      return (
        <>{`Membership term ${isPast ? 'started' : 'starts'}${
          isMembershipEndless ? ' without an end date' : ''
        }`}</>
      );
    default:
      return <>Unknown</>;
  }
};

const AutoPayMembershipTermImportantDateType: React.FC<{
  importantDateType: AutoPayImportantDateType;
  numPaymentsRepresented: number | null;
  hasRelevantFailedPayment: boolean;
}> = ({
  importantDateType,
  numPaymentsRepresented,
  hasRelevantFailedPayment,
}) => {
  switch (importantDateType) {
    case AutoPayImportantDateType.MONTHLY:
      if (hasRelevantFailedPayment) {
        return (
          <HStack>
            <Text>
              Monthly payment auto-pay - Payment failed for{' '}
              {numPaymentsRepresented}
              {numPaymentsRepresented === 1 ? ' month' : ' months'}
            </Text>
            <InfoIcon ml="2" color="red.500" />
          </HStack>
        );
      } else {
        return <>Upcoming monthly payment auto-pay</>;
      }
    case AutoPayImportantDateType.RENEWAL:
    case AutoPayImportantDateType.MONTHLY_RENEWAL:
      return (
        <HStack spacing="1">
          <Text>Renewal auto-pay</Text>
          {hasRelevantFailedPayment && (
            <>
              <Text>initiated</Text>
              <InfoIcon ml="2" color="red.500" />
            </>
          )}
        </HStack>
      );

    default:
      return <>Unknown</>;
  }
};

const PaymentMembershipTermImportantDateType: React.FC<{
  importantDateType: PaymentImportantDateType;
  isPast: boolean;
}> = ({ importantDateType, isPast }) => {
  switch (importantDateType) {
    case PaymentImportantDateType.MEMBERSHIP_FEE:
      return <>{isPast ? 'Membership fee was due' : 'Membership fee due'}</>;
    case PaymentImportantDateType.RENEWAL_PERIOD_START:
      return <>{isPast ? 'Renewal period started' : 'Renewal period starts'}</>;
    case PaymentImportantDateType.RENEWAL_DUE:
      return <>Renewal due</>;
    case PaymentImportantDateType.INVOICE_DUE:
      return <>Invoice due</>;
    default:
      return <>Unknown</>;
  }
};

ImportantDateItem.importantDateFragment = gql`
  fragment ImportantDateItem_MembershipTermImportantDateFragment on MembershipTermImportantDate {
    date
    ... on SimpleMembershipTermImportantDate {
      date
      type
    }
    ... on PaymentMembershipTermImportantDate {
      date
      annualPrice
      monthlyPrice
      paymentType
      order {
        _id
        paymentReceived
        paymentDue
        creditToApply
        invoice {
          number
          pdfUrl
        }
      }
    }
    ... on AutoPayMembershipTermImportantDate {
      date
      price
      autoPayType
      numPaymentsRepresented
      order {
        _id
        paymentReceived
        paymentDue
        creditToApply
      }
    }
  }
`;
